import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import {
  Activity,
  AnyEntityId,
  EntityType,
  EntityTypesSupportingRoutes,
} from "@mapmycustomers/shared/types/entity";
import { Alert } from "@mapmycustomers/ui";

import CompanyAssociation from "@app/component/associations/CompanyAssociation";
import PeopleAssociation from "@app/component/associations/PeopleAssociation";
import { showEntityView } from "@app/store/entityView/actions";
import { RootState } from "@app/store/rootReducer";

import { getEntityType, getInvalidStops } from "../../store";
import { updateActivityAssociation } from "../../store/actions";

import styles from "./Adjust.module.scss";
import Stop from "./Stop";

interface Props {
  entityType: EntityTypesSupportingRoutes;
  invalidStops: Activity[];
  onEdit?: (payload: { entityId: AnyEntityId; entityType: EntityTypesSupportingRoutes }) => void;
  onUpdateActivityAssociation?: (payload: {
    activity: Activity;
    entityId: AnyEntityId;
    entityType: EntityTypesSupportingRoutes;
  }) => void;
}

const InvalidStops: React.FC<Props> = ({
  entityType,
  invalidStops,
  onEdit,
  onUpdateActivityAssociation,
}) => {
  const intl = useIntl();

  const [updatedActivity, setUpdatedActivity] = useState<Activity | undefined>();

  const handleEdit = useCallback(
    ({ entity, entityType }) => {
      onEdit?.({ entityId: entity.id, entityType });
    },
    [onEdit]
  );

  const handleHideAssociationModal = useCallback(() => setUpdatedActivity(undefined), []);

  const handleSelectAssociation = useCallback(
    ([entityId]: AnyEntityId[]) => {
      onUpdateActivityAssociation?.({ activity: updatedActivity!, entityId, entityType });
    },
    [entityType, onUpdateActivityAssociation, updatedActivity]
  );

  return (
    <div>
      {invalidStops.length > 0 && (
        <Alert
          className={styles.alert}
          message={intl.formatMessage(
            {
              id: "activities.calendarView.routeBuilder.steps.adjust.alert.invalid",
              defaultMessage:
                "Activities will not be included unless valid {entityType, select, accounts {Companies} contacts {People} other {Records}} are associated.",
              description:
                "Invalid stops alert on Adjust step in Route Builder in Activities Calendar view",
            },
            {
              entityType,
            }
          )}
          type="warning"
        />
      )}
      <div>
        {invalidStops.map((activity) => (
          <Stop
            activity={activity}
            entityType={entityType}
            key={activity.id}
            onAdd={setUpdatedActivity}
            onEdit={handleEdit}
          />
        ))}
      </div>
      {updatedActivity &&
        (entityType === EntityType.COMPANY ? (
          <CompanyAssociation
            entityType={entityType}
            multiselect={false}
            onHide={handleHideAssociationModal}
            onSelect={handleSelectAssociation}
          />
        ) : (
          <PeopleAssociation
            multiselect={false}
            onHide={handleHideAssociationModal}
            onSelect={handleSelectAssociation}
          />
        ))}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  entityType: getEntityType(state),
  invalidStops: getInvalidStops(state),
});

const mapDispatchToProps = {
  onEdit: showEntityView,
  onUpdateActivityAssociation: updateActivityAssociation.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(InvalidStops);
